// Presets + the top bar — 05i-1. main.js's clearWorkspace / loadPreset (the "All" preset and
// the saved dashboards) and the top bar's buttons, copied exactly. The pure sums are
// preset-rules.ts, the dashboards' data saved-presets.ts.
//
// Every module a preset builds gets its `_`-state before its first render (createModule's
// `custom`, 05i-1): one render each, where main.js rendered, assigned the state and
// rendered again.
//
// The top bar's markup is legacy-scaffold.html's (byte-identical); this only adds the
// listeners main.js added. Installed just before main.js loads, where main.js added them.

import { STEP } from "./placement.js";
import { getGridCols, resetWorkspaceGridRows, scrollToTopLeft, setWorkspaceGridRows } from "./canvas-space.js";
import { readTemplateContext } from "./menu-drag-rules.js";
import { allPresetLayout, savedPresetModules, type ModuleDef } from "./preset-rules.js";
import { SAVED_PRESETS } from "./saved-presets.js";
import { canvasApi } from "./shell-actions.js";
import { getDefaultSize, getModuleConfigSync, templateModuleSize, whenModulesReady } from "../modules/module-config.js";
import { setStickyPlayer } from "./sticky-player.js";
import { undoApi } from "./undo.js";
import { dismissWelcomeScreen } from "./welcome.js";
import { LIVE } from "../live/flag.js";
import { LIVE_DEFAULT_GOALKEEPER, LIVE_DEFAULT_PLAYER } from "../live/default-players.js";

// This project's demo players: McCabe has full shot / pass / match data end to end (not
// just Bio / Stats / Radar like the other heroes), and van Domselaar is the goalkeeper one.
// By id (ids-1). The live site opens on its own pair instead (live/default-players.ts).
const ALL_PRESET_PLAYER = LIVE ? LIVE_DEFAULT_PLAYER.id : 15620; // Katie Alison McCabe
const ALL_PRESET_GOALKEEPER = LIVE ? LIVE_DEFAULT_GOALKEEPER.id : 32339; // Daphne van Domselaar

function byId(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`#${id} missing from legacy-scaffold.html`);
  return el;
}

/** Empty the canvas: no modules, no welcome screen, the canvas back to its size, no shift. */
export function clearWorkspace(): void {
  canvasApi().clearModules();
  dismissWelcomeScreen();
  resetWorkspaceGridRows();
  canvasApi().setWorkspaceShift(0, 0);
}

type AllPresetDef = ModuleDef & { context: Record<string, string> | null };

/** One of every side-panel tile, group by group, as the side panel orders them. Read off the
 *  page, as main.js did — so tiles the module search has moved out of their group are
 *  missed (kept). */
function sidePanelGroups(): AllPresetDef[][] {
  const gridCols = getGridCols();
  return Array.from(byId("side-panel").querySelectorAll(".menu-title"))
    .map((titleEl) => {
      const grid = titleEl.nextElementSibling;
      // A live-only tile (the Passing Report) isn't on the snapshot site; a snapshot-only one (Action
      // Map: its data is the snapshot's, step 1) isn't on ?live=1.
      return grid ? Array.from(grid.querySelectorAll(LIVE ? ".module-template:not([data-snapshot-only])" : ".module-template:not([data-live-only])")) : [];
    })
    .filter((templates) => templates.length > 0)
    .map((templates) =>
      templates.map((t) => {
        const size = templateModuleSize(t);
        return {
          title: t.getAttribute("data-title") ?? "",
          cols: Math.max(1, Math.min(gridCols, Number(size.cols) || 1)),
          rows: Math.max(1, Number(size.rows) || 1),
          // Four "… Rank" tiles share the title "Ranking" and differ only by their
          // data-context-* — Ben fixed "All" dropping it (they all landed blank).
          context: readTemplateContext(Array.from(t.attributes)),
        };
      }),
    );
}

function loadAllPreset(): void {
  // Before any module exists, so every picker opens on her (not whoever sorts first).
  setStickyPlayer(ALL_PRESET_PLAYER);
  const { items, bottomRow } = allPresetLayout(sidePanelGroups(), getGridCols());
  setWorkspaceGridRows(bottomRow + 4);
  items.forEach((item) => {
    // Goalkeeping only takes real goalkeepers, so McCabe never fits there: an explicit
    // player for this one build (not a second sticky default).
    const player = getModuleConfigSync(item.title)?.categoryId === "goalkeeping" ? ALL_PRESET_GOALKEEPER : null;
    canvasApi().createModule({
      x: item.col * STEP,
      y: item.row * STEP,
      cols: item.cols,
      rows: item.rows,
      title: item.title,
      player,
      custom: item.context,
    });
  });
}

function loadSavedPreset(key: string): void {
  const preset = SAVED_PRESETS[key];
  // An unknown key loads nothing (the canvas stays cleared) — main.js's behaviour.
  if (!preset) return;
  const { modules, bottomRow } = savedPresetModules(preset, (title, cols, rows) => getDefaultSize(title, { cols, rows }));
  setWorkspaceGridRows(bottomRow + 4);
  modules.forEach((m) => {
    canvasApi().createModule({
      x: m.col * STEP,
      y: m.row * STEP,
      cols: m.cols,
      rows: m.rows,
      title: m.title,
      player: m.player,
      custom: m.custom,
    });
  });
}

/**
 * Replace the canvas with a preset ("all") or a saved dashboard. Takes no checkpoint —
 * the caller does (a button: before; a dashboard card's drop: its drag's first move).
 *
 * Async like main.js's: it waits for modules.json, so even when that's loaded the canvas
 * changes a microtask later — after a card drop's own collapseWorkspace, which relies on it.
 */
export async function loadPreset(presetKey: string): Promise<void> {
  await whenModulesReady();
  clearWorkspace();
  if (presetKey === "all") loadAllPreset();
  else loadSavedPreset(presetKey);
  requestAnimationFrame(() => {
    // The phone layout scrolls to the top instead (mobile JS goes in 05k).
    if (window.innerWidth <= 768) window.scrollTo({ top: 0, behavior: "instant" });
    else scrollToTopLeft();
  });
}

/** The top bar: the preset buttons ("All", also the phone header's), Clear, Save, Refresh and
 *  the theme toggle. (Login is a stub with no listener; Undo is undo.ts's.) */
export function installTopBar(): void {
  document.querySelectorAll<HTMLElement>(".preset-btn[data-preset]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const key = btn.getAttribute("data-preset");
      if (key) {
        undoApi().saveState();
        void loadPreset(key);
      }
    });
  });

  byId("clear-btn").addEventListener("click", () => {
    undoApi().saveState();
    clearWorkspace();
  });

  // Swap only the label's text node, not the whole button — replacing textContent used to
  // wipe the icon for good (Track 1 review item 7, fixed per Ben 2026-10-02). The icon
  // stays put through "Saved!" too.
  const saveBtn = byId("save-btn");
  const saveLabel = [...saveBtn.childNodes].find((n) => n.nodeType === Node.TEXT_NODE && n.textContent?.trim());
  if (!saveLabel) throw new Error("#save-btn has no text label (legacy-scaffold.html)");
  saveBtn.addEventListener("click", () => {
    saveLabel.textContent = "Saved!";
    setTimeout(() => {
      saveLabel.textContent = "Save";
    }, 1200);
  });

  // Re-render every module from its `_`-state (the widgets that rebuild on a refresh lose
  // their on-screen-only state, as the legacy renderers did — bridge.tsx).
  byId("refresh-btn").addEventListener("click", () => {
    document.querySelectorAll<HTMLElement>(".placed-module").forEach((mod) => canvasApi().refreshModuleBody(mod));
  });

  // index.html's inline script applies the saved choice before the first paint.
  const html = document.documentElement;
  byId("theme-toggle").addEventListener("click", () => {
    if (html.getAttribute("data-theme") === "light") {
      html.removeAttribute("data-theme");
      localStorage.setItem("bento-theme", "dark");
    } else {
      html.setAttribute("data-theme", "light");
      localStorage.setItem("bento-theme", "light");
    }
  });
}

